import { Button, Link as AmplifyLink, Grid } from '@aws-amplify/ui-react';
import { useState } from 'react';
import { FaArrowLeft, FaExternalLinkAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import General from '../utils/GeneralUtils';
import { CopyToClipboard } from './CopyToClipboard';
import { EmailFormatTag } from './EmailFormatTag';
import Currency from './Currency';
import GradeSpan from "./GradeSpan";
import { InstitutionDomainEmailFormats, InstitutionEmailFormatsSlideout } from './InstitutionEmailFormatsSlideout';
import './InstitutionTopCard.scss';
import NA from './NA';
import PhysicalAddress from "./PhysicalAddress";

enum CardType {
  DISTRICT = 'DISTRICT',
  AGENCY = 'AGENCY',
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
}

/**
 * The leading pattern of the institution's largest domain, with everything else — the
 * alternates and any secondary domains — behind a drawer. The API has already dropped
 * patterns seen on five addresses or fewer and capped the list at three domains.
 */
function EmailFormatRows({ institution }: { institution: any }) {
  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);

  const domains: InstitutionDomainEmailFormats[] = (institution.email_formats ?? [])
    .filter((it: InstitutionDomainEmailFormats) => it.formats.length > 0);

  if (domains.length === 0) return null;

  const top = domains[0];
  const topFormat = top.formats[0];
  const others = domains.reduce((n, it) => n + it.formats.length, 0) - 1;

  return <>
    <div className='row'>
      <span className='narrow label'>Email Format</span>
      <EmailFormatTag
        format={topFormat.emailFormat}
        domain={top.domain}
        percentage={topFormat.percentage}
        contactCount={topFormat.contactCount}
        domainTotal={top.domainTotal}
        size='small'
      />
      {others > 0 && <button
        type='button'
        className='institution-card__more'
        onClick={() => setIsSlideoutOpen(true)}
      >+{others} more</button>}
    </div>
    <InstitutionEmailFormatsSlideout
      isOpen={isSlideoutOpen}
      onClose={() => setIsSlideoutOpen(false)}
      institutionName={institution.name}
      emailFormats={domains}
    />
  </>;
}

function getCardType(institution: any): CardType {
  if (institution.type === 'DISTRICT' || institution.type === 'PARENT_ORG') {
    return CardType.DISTRICT
  } else if (institution.type === 'AGENCY') {
    return CardType.AGENCY
  } else if (institution.type === 'SCHOOL') {
    if (institution.access === 'PRIVATE') {
      return CardType.PRIVATE
    }
  }
  return CardType.PUBLIC
}

const props = {
  [CardType.DISTRICT]: {
    name: 'District',
    classes: 'institution-card-district'
  },
  [CardType.AGENCY]: {
    name: 'Service Agency',
    classes: 'institution-card-agency'
  },
  [CardType.PUBLIC]: {
    name: 'Public School',
    classes: 'institution-card-public-school'
  },
  [CardType.PRIVATE]: {
    name: 'Private School',
    classes: 'institution-card-private-school'
  },
}

export const subTypeMap: any = {
  'REGULAR': 'Regular',
  'CHARTER': 'Charter',
  'SPECIALIZED': 'Specialized',
  'SERVICE': 'Service',
  'OTHER': 'Other',
  'SUPERVISORY': 'Supervisory',
  'STATE': 'State',
  'COMPONENT': 'Component',
  'FEDERAL': 'Federal',
  'ALTERNATIVE': 'Alternative',
}

interface InstitutionTopCardProps {
  institution: any;
  roundedBottomCorners?: boolean;
  onExport?: () => void;
  isExportLoading?: boolean;
}

export default function InstitutionTopCard(input: InstitutionTopCardProps) {
  const institution = input.institution;
  const roundedBottomCorners = input.roundedBottomCorners ?? true;
  const onExport = input.onExport;
  const isExportLoading = input.isExportLoading;

  const cardType = getCardType(institution);
  const cardTypeProps = props[cardType];

  return <div className={`institution-card dark ${ cardTypeProps.classes } ${!roundedBottomCorners ? 'no-bottom-radius' : ''}`}>

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 10 }}>
      <div>
        {/* Show parent institution link */}
        {institution.parent?.id && institution.parent?.name && (
          <div className='parent-link'>
            <Link to={`/directory/institutions/${institution.parent.id}`}>
              <FaArrowLeft size={12} style={{ marginRight: 3 }} /> {institution.parent.name}
            </Link>
          </div>
        )}
        <h1 style={{ margin: 0, marginBottom: 0 }}>{institution.name}</h1>
      </div>
      {onExport && (
        <Button
          onClick={onExport}
          size="small"
          className="tiny"
          isLoading={isExportLoading}
          isDisabled={isExportLoading}
        >
          Export
        </Button>
      )}
    </div>

    <Grid templateColumns="1fr 1fr">
      <div className='side'>
        {
          ([CardType.PRIVATE, CardType.PUBLIC].includes(cardType)) && <div className='row'><GradeSpan row={institution} /></div>
        }
        {
          cardType === CardType.PRIVATE &&
            <>
              <div className='row'><span className='label'>Access</span> Private</div>
              <div className='row'><span className='label'>Religious Affiliation</span> { institution.religious_affiliation ? General.getHumanReadableReligiousAffiliation(institution.religious_affiliation) : 'None' }</div>
              <div className='row'><span className='label'>Annual Tuition</span> <Currency amount={institution.tuition_annual} /></div>
              { institution?.nr_staff > 0 && <div className='row'><span className='label'>Nr. Staff</span> { !institution.nr_staff ? <NA /> : General.addCommasToNumber(institution.nr_staff) }</div> }
              { institution?.nr_students > 10 && <div className='row'><span className='label'>Nr. Students</span> { !institution.nr_students ? <NA /> : General.addCommasToNumber(institution.nr_students) }</div> }
            </>
        }
        {
          cardType === CardType.PUBLIC && (
            <>
              <div className='row'><span className='label'>Access</span> Public</div>
              {institution.subtype && <div className='row'><span className='label'>Subtype</span> {subTypeMap?.[institution.subtype] ?? institution.subtype}</div>}
            </>
          )
        }
        {
          cardType === CardType.DISTRICT &&
            <>
              <div className='row'><span className='label'>Type</span> { subTypeMap?.[institution['subtype']] ?? institution['subtype'] }</div>
              {
                institution['revenue_total']
                  ? <div className='row'><span className='label'>Annual Revenue</span> <Currency amount={institution['revenue_total']} /></div>
                  : <></>
              }
              <div className='row'><span className='label'>Nr. Staff</span> { !institution['nr_staff'] ? <NA /> : General.addCommasToNumber(institution['nr_staff']) }</div>
              <div className='row'><span className='label'>Nr. Students</span> { !institution['nr_students'] ? <NA /> : General.addCommasToNumber(institution['nr_students']) }</div>
            </>
        }
        {
          cardType === CardType.AGENCY &&
            <>
              <div className='row'><span className='label'>Type</span> Service Agency</div>
              {
                institution['revenue_total']
                  ? <div className='row'><span className='label'>Annual Revenue</span> <Currency amount={institution['revenue_total']} /></div>
                  : <></>
              }
              <div className='row'><span className='label'>Nr. Institutions</span> { !institution['nr_child_institutions'] ? <NA /> : General.addCommasToNumber(institution['nr_child_institutions']) }</div>
              <div className='row'><span className='label'>Nr. Staff</span> { !institution['nr_staff'] ? <NA /> : General.addCommasToNumber(institution['nr_staff']) }</div>
              <div className='row'><span className='label'>Nr. Students</span> { !institution['nr_students'] ? <NA /> : General.addCommasToNumber(institution['nr_students']) }</div>
            </>
        }
      </div>
      <div className='side'>
        {
          ([CardType.PRIVATE, CardType.PUBLIC, CardType.DISTRICT, CardType.AGENCY].includes(cardType)) &&
            <>
              {/* <div className='row tall'><strong>{ General.getStateName(institution.locState) }</strong></div> */}
              { (institution.loc_address_phys || institution.locAddressPhys) && <div className='row'><PhysicalAddress row={institution} isLink={true} /></div> }
              { institution?.website?.url && <div className='row'><AmplifyLink target='_blank' href={ institution.website.url }>{ General.ellipsisInMiddle(institution.website.url, 56) } <FaExternalLinkAlt size={11} /></AmplifyLink></div>}
            </>
        }
        <div className='row'><span className='narrow label'>ID</span> { institution.id } <CopyToClipboard value={institution.id} title="Copy ID" /></div>
        { institution.id_nces_school && <div className='row'><span className='narrow label'>School NCES ID</span> { institution.id_nces_school } <CopyToClipboard value={institution.id_nces_school} title="Copy School NCES ID" /></div> }
        { institution.id_nces_district && <div className='row'><span className='narrow label'>District NCES ID</span> { institution.id_nces_district } <CopyToClipboard value={institution.id_nces_district} title="Copy District NCES ID" /></div> }
        { institution.phone && <div className='row'><span className='narrow label'>Phone</span> { institution.phone }</div> }
        { institution.fax && <div className='row'><span className='narrow label'>Fax</span> { institution.fax }</div> }
        <EmailFormatRows institution={institution} />
      </div>
    </Grid>
  </div>
}
